import React from 'react';

/**
 * Segmented progress — real units of work (parts researched, emails sent),
 * never a percentage of one AI call. Green done · violet running (Robbie is
 * on them) · grey waiting · amber needs attention. Legend counts are mono.
 */
export interface ProgressCounts {
  done: number;
  running: number;
  waiting: number;
  /** Units that need the buyer (failed, stuck). Hidden when 0. */
  attention?: number;
}

export interface ProgressLabels {
  done?: string;
  running?: string;
  waiting?: string;
  attention?: string;
}

export interface SegmentedProgressProps {
  counts: ProgressCounts;
  labels?: ProgressLabels;
  /** Hide the legend (the strip shows its own summary). */
  showLegend?: boolean;
  /** Bar height: 6px (strip) or 8px (pop-up). */
  thick?: boolean;
  /** `inline`: bar (150px) and legend side by side, for strips. */
  inline?: boolean;
  className?: string;
}

const DEFAULT_LABELS: Required<ProgressLabels> = {
  done: 'done',
  running: 'researching',
  waiting: 'waiting their turn',
  attention: 'need a look',
};

const KEYS = ['done', 'running', 'waiting', 'attention'] as const;
const FILL: Record<(typeof KEYS)[number], string> = {
  done: 'bg-success-ink',
  running: 'bg-robbie',
  waiting: 'bg-line-strong',
  attention: 'bg-warn-ink',
};

export const SegmentedProgress: React.FC<SegmentedProgressProps> = ({
  counts,
  labels,
  showLegend = true,
  thick = false,
  inline = false,
  className = '',
}) => {
  const names = { ...DEFAULT_LABELS, ...labels };
  const total = KEYS.reduce((sum, k) => sum + (counts[k] ?? 0), 0);
  const shown = KEYS.filter((k) => (counts[k] ?? 0) > 0);
  const summary = shown.map((k) => `${counts[k]} ${names[k]}`).join(', ');
  return (
    <div className={`flex ${inline ? 'flex-wrap items-center gap-s5' : 'flex-col gap-s4'} ${className}`}>
      <div
        role="img"
        aria-label={summary || 'Nothing to show yet'}
        className={`flex gap-[2px] overflow-hidden rounded-round bg-line-divider-soft ${thick ? 'h-2' : 'h-[6px]'} ${inline ? 'w-[150px] shrink-0' : ''}`}
      >
        {total > 0 &&
          shown.map((k) => (
            <i key={k} data-segment={k} className={`block h-full ${FILL[k]}`} style={{ width: `${((counts[k] ?? 0) / total) * 100}%` }} />
          ))}
      </div>
      {showLegend && (
        <div aria-hidden="true" className="flex flex-wrap gap-x-s6 gap-y-s1 text-body-sm text-ink-secondary">
          {shown.map((k) => (
            <span key={k} className="inline-flex items-center gap-s2">
              <span className={`h-2 w-2 rounded-[2px] ${FILL[k]}`} />
              <b className="font-mono text-[11.5px] font-semibold text-ink">{counts[k]}</b>
              {names[k]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SegmentedProgress;
